/**
 * Phase A4 — /reset-password?token=X&role=Y&context=forgot|setup
 *
 * Mirrors SetPassword but authenticates via the URL token (not a
 * session cookie). On success the backend mints a fresh role session
 * cookie and returns redirect_to.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { passwordReset, validatePassword } from "../lib/api";
import { track } from "../lib/analytics";
import BackLink from "../components/BackLink";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import "./SetPassword.css";

const VALIDATE_DEBOUNCE_MS = 300;
const MIN_LEN = 8;

export default function ResetPassword() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const rawRole = params.get("role");
    const role = rawRole === "employee"
        ? "employee"
        : rawRole === "admin" ? "admin" : "employer";
    const context = params.get("context") === "setup" ? "setup" : "forgot";

    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState("");
    const [validation, setValidation] = useState({
        valid: false, breached: false, length_ok: false, checking: false,
    });
    const debounceRef = useRef(null);
    const pwInputRef = useRef(null);

    useEffect(() => {
        track("reset_password_view", { role, context });
        document.title = t(`auth.reset.heading.${context}`);
        pwInputRef.current && pwInputRef.current.focus();
    }, [role, context, t]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (pw1.length < MIN_LEN) {
            setValidation({ valid: false, breached: false, length_ok: false, checking: false });
            return;
        }
        setValidation((v) => ({ ...v, length_ok: true, checking: true }));
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await validatePassword(pw1);
                setValidation({ ...res, checking: false });
            } catch (_) {
                setValidation({
                    valid: true, breached: false, length_ok: true, checking: false,
                });
            }
        }, VALIDATE_DEBOUNCE_MS);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [pw1]);

    const matchState = useMemo(() => {
        if (pw2.length === 0) return "empty";
        return pw1 === pw2 ? "match" : "mismatch";
    }, [pw1, pw2]);

    const canSubmit = !submitting
        && token.length > 0
        && validation.length_ok
        && !validation.breached
        && !validation.checking
        && matchState === "match"
        && submitErr !== "expired";

    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitErr("");
        setSubmitting(true);
        try {
            const res = await passwordReset({ role, token, newPassword: pw1 });
            track("reset_password_ok", { role, context });
            window.location.replace(res?.redirect_to || "/portal");
        } catch (error) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            if (status === 400 && detail === "invalid_or_expired_reset_link") {
                setSubmitErr("expired");
            } else if (status === 400 && detail === "password_breached") {
                setValidation((v) => ({ ...v, breached: true }));
                setSubmitErr("breached");
            } else if (status === 400 && detail === "password_too_short") {
                setSubmitErr("tooShort");
            } else {
                setSubmitErr("network");
            }
            track("reset_password_error", { role, context, status, detail });
        } finally {
            setSubmitting(false);
        }
    }

    if (!token) {
        return (
            <section className="set-password" data-testid="reset-password-no-token">
                <div className="set-password-inner">
                    <p className="set-password-eyebrow mono">
                        {t(`auth.reset.eyebrow.${context}`)}
                    </p>
                    <div className="set-password-error-banner">
                        {t("auth.reset.errors.expired")}
                    </div>
                    <Link
                        to={`/forgot-password?role=${role}`}
                        className="set-password-secondary-link"
                        data-testid="reset-password-request-new"
                    >
                        {t("auth.reset.errors.requestNew")}
                    </Link>
                </div>
            </section>
        );
    }

    const backTo = role === "employer"
        ? "/employer-signin"
        : role === "admin" ? "/admin/login" : "/apply";

    const feedbackKey = (() => {
        if (validation.breached) return "auth.setPassword.feedback.breached";
        if (pw1.length > 0 && !validation.length_ok) {
            return "auth.setPassword.feedback.tooShort";
        }
        if (validation.length_ok && !validation.checking) {
            return "auth.setPassword.feedback.longEnough";
        }
        return null;
    })();
    const feedbackTone = validation.breached
        ? "warn" : validation.length_ok ? "ok" : "muted";
    const matchTone = matchState === "empty"
        ? "muted" : matchState === "match" ? "ok" : "warn";

    return (
        <section className="set-password" data-testid="reset-password-page">
            <div className="set-password-backrow">
                <BackLink to={backTo} testId="reset-password-back" />
            </div>

            <div className="set-password-inner">
                <p className="set-password-eyebrow mono">
                    {t(`auth.reset.eyebrow.${context}`)}
                </p>
                <h1 className="set-password-title">
                    {t(`auth.reset.heading.${context}`)}
                </h1>
                <p className="set-password-sub">
                    {t(`auth.reset.subhead.${context}`)}
                </p>

                {submitErr === "expired" && (
                    <div
                        className="set-password-error-banner"
                        role="status"
                        data-testid="reset-password-expired"
                    >
                        {t("auth.reset.errors.expired")}{" "}
                        <Link
                            to={`/forgot-password?role=${role}`}
                            className="set-password-secondary-link"
                            data-testid="reset-password-request-new"
                        >
                            {t("auth.reset.errors.requestNew")}
                        </Link>
                    </div>
                )}
                {submitErr === "breached" && (
                    <div className="set-password-error-banner" role="status">
                        {t("auth.setPassword.feedback.breached")}
                    </div>
                )}
                {submitErr === "network" && (
                    <div className="set-password-error-banner" role="status">
                        {t("auth.reset.errors.network")}
                    </div>
                )}

                <form
                    className="set-password-form"
                    onSubmit={handleSubmit}
                    autoComplete="on"
                    data-testid="reset-password-form"
                    noValidate
                >
                    <label className="set-password-label mono" htmlFor="reset-password-new">
                        {t("auth.setPassword.newPasswordLabel")}
                    </label>
                    <input
                        ref={pwInputRef}
                        id="reset-password-new"
                        type="password"
                        name="new-password"
                        autoComplete="new-password"
                        minLength={MIN_LEN}
                        required
                        value={pw1}
                        onChange={(e) => setPw1(e.target.value)}
                        className="set-password-input"
                        data-testid="reset-password-new"
                    />
                    <div
                        className={`set-password-feedback set-password-feedback-${feedbackTone}`}
                        aria-live="polite"
                        data-testid="reset-password-feedback"
                    >
                        {feedbackKey ? t(feedbackKey, { min: MIN_LEN }) : "\u00A0"}
                    </div>

                    <label className="set-password-label mono" htmlFor="reset-password-confirm">
                        {t("auth.setPassword.confirmPasswordLabel")}
                    </label>
                    <input
                        id="reset-password-confirm"
                        type="password"
                        name="confirm-password"
                        autoComplete="new-password"
                        required
                        value={pw2}
                        onChange={(e) => setPw2(e.target.value)}
                        className="set-password-input"
                        data-testid="reset-password-confirm"
                    />
                    <div
                        className={`set-password-feedback set-password-feedback-${matchTone}`}
                        aria-live="polite"
                    >
                        {matchState === "mismatch"
                            ? t("auth.setPassword.feedback.mismatched")
                            : matchState === "match"
                                ? t("auth.setPassword.feedback.matched")
                                : "\u00A0"}
                    </div>

                    <button
                        type="submit"
                        className="set-password-submit"
                        disabled={!canSubmit}
                        data-testid="reset-password-submit"
                    >
                        {submitting
                            ? t("auth.reset.submitting")
                            : t(`auth.reset.submit.${context}`)}
                    </button>
                </form>

                <div className="set-password-secondary">
                    <a
                        href={CALENDLY_SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="set-password-secondary-link"
                    >
                        {t("auth.signin.errors.contactUs")}
                    </a>
                </div>
            </div>
        </section>
    );
}
